/**
 * `POST /api/command`.
 *
 * Always resolves with a `CommandIntent`, never rejects on a model failure — the server
 * answers `{ kind: 'unclear' }` for anything it could not turn into a command, and this
 * does the same for a malformed response or a dropped connection. There is one code path
 * in the store for "we did not understand", and it is the right behaviour for an outage
 * too: say so quietly, change nothing.
 *
 * A 4xx (a malformed request body) still throws, because that is a bug in the caller
 * rather than a thing the player said.
 *
 * ## Why the validator is loaded lazily
 *
 * `CommandIntentSchema` is the client's only Zod use, and Zod was 78 kB of a 566 kB main
 * bundle sitting in front of every first load — including every load that never opens the
 * command bar. `commandIntentParse.js` is therefore imported at call time and cached: the
 * download happens once, in parallel with a network round trip that is already in flight,
 * and a failure to fetch that chunk degrades to `unclear` like every other failure here.
 */

import type { CommandIntent } from '@hc/shared';
import {
  parseDebugPayload,
  postJson,
  type ApiOptions,
  type CommandRequest,
  type LlmDebugPayload,
} from './types.js';

const UNCLEAR: CommandIntent = { kind: 'unclear' };

type IntentParser = (value: unknown) => CommandIntent | null;

let parserPromise: Promise<IntentParser> | null = null;

/** The validator chunk, fetched once. A failed fetch is not cached, so a flaky network retries. */
function loadParser(): Promise<IntentParser> {
  parserPromise ??= import('./commandIntentParse.js')
    .then((module) => module.parseCommandIntent)
    .catch((error: unknown) => {
      parserPromise = null;
      throw error;
    });
  return parserPromise;
}

export interface InterpretCommandOptions extends ApiOptions {
  /**
   * Called when the response carried the composed prompt, which it does only when the
   * request asked. A callback rather than a second return value, so the one thing this
   * function promises — always an intent, never a rejection — is unchanged.
   */
  onDebug?: (debug: LlmDebugPayload) => void;
}

export async function interpretCommand(
  request: CommandRequest,
  options?: InterpretCommandOptions,
): Promise<CommandIntent> {
  // Started before the request rather than after it, so the chunk downloads inside the
  // latency of the call it is going to validate. A failure to load it is just one more
  // reason we did not understand.
  const parsing = loadParser().catch(() => null);

  const response = await postJson('/api/command', request, options, 'application/json');

  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return UNCLEAR;
  }

  if (!body || typeof body !== 'object') return UNCLEAR;

  // Reported before the intent is validated: a body we could not turn into a command is
  // exactly the one somebody with the prompts on show wants to read.
  const debug = parseDebugPayload((body as { debug?: unknown }).debug);
  if (debug) options?.onDebug?.(debug);

  const parse = await parsing;
  if (!parse) return UNCLEAR;
  // Validated client-side as well as server-side: free text must never reach the
  // reducer, and "the server said so" is not a reason to skip the check.
  return parse((body as { intent?: unknown }).intent) ?? UNCLEAR;
}
